import React, {
  useState,
  ChangeEvent,
  FocusEvent,
  FormEvent,
  useEffect,
} from "react";
import classNames from "classnames/bind";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import styles from "./account-security.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { togglePopup } from "../../../../../slices/utilsSlice";
import { addToast } from "../../../../../slices/toastSlice";
import { updateUser } from "../../../../../slices/sessionSlice";
import { RootState } from "../../../../../store";
import { isCognitoError, getErrorMessage } from "../../../../../utils/error-utils";
import { useSetPasswordMutation, useChangePasswordMutation } from "../../../../../api/authApi";
import { EyeIcon } from "../../../../authorization/AuthIcons";
import { tokens } from "../../../../../styles/tokens";

const cx = classNames.bind(styles);

interface AccountSecurityProps {
  onPasswordChange?: (data: { password: string; newPassword: string }) => void;
}

const AccountSecurity: React.FC<AccountSecurityProps> = ({
  onPasswordChange = () => {},
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.session.user);
  const idToken = useSelector((state: RootState) => state.session.idToken);
  const [setPasswordApi] = useSetPasswordMutation();
  const [changePasswordApi] = useChangePasswordMutation();

  const isGoogleOnly = user?.auth_provider === "google";

  const [formData, setFormData] = useState({
    password: "",
    newPassword: "",
    passwordConfirmation: "",
  });
  const [errors, setErrors] = useState({
    passwordError: false,
    newPasswordError: false,
    passwordConfirmationError: false,
  });
  const [passwordValidationErrors, setPasswordValidationErrors] = useState<
    string[]
  >([]);
  const [isSame, setIsSame] = useState<boolean | undefined>(undefined);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!formData.newPassword) {
      setPasswordStrength(0);
      return;
    }
    let strength = 0;
    if (formData.newPassword.length >= 8) strength += 1;
    if (/\d/.test(formData.newPassword)) strength += 1;
    if (/[a-z]/.test(formData.newPassword)) strength += 1;
    if (/[A-Z]/.test(formData.newPassword)) strength += 1;
    setPasswordStrength(strength);
  }, [formData.newPassword]);

  const getStrengthLabel = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return t("accountSecurity.weak", "Weak");
      case 2:
        return t("accountSecurity.fair", "Fair");
      case 3:
        return t("accountSecurity.good", "Good");
      case 4:
        return t("accountSecurity.strong", "Strong");
      default:
        return "";
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return tokens.err;
      case 2:
        return tokens.warn;
      case 3:
        return tokens.okFill;
      case 4:
        return tokens.indigo600;
      default:
        return "#e0e0e0";
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "newPassword") {
      const validationResult = validatePassword(value);
      setPasswordValidationErrors(validationResult.errors);
      setErrors((prev) => ({
        ...prev,
        newPasswordError: !validationResult.isValid,
      }));
    } else if (name === "passwordConfirmation") {
      setIsSame(value === formData.newPassword);
      setErrors((prev) => ({
        ...prev,
        passwordConfirmationError:
          value !== formData.newPassword && value !== "",
      }));
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (name === "password") {
      setErrors((prev) => ({ ...prev, passwordError: false }));
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "newPassword" && value) {
      const validationResult = validatePassword(value);
      setPasswordValidationErrors(validationResult.errors);
      setErrors((prev) => ({
        ...prev,
        newPasswordError: !validationResult.isValid,
      }));
    }

    if (name === "passwordConfirmation" && value) {
      setIsSame(value === formData.newPassword);
      setErrors((prev) => ({
        ...prev,
        passwordConfirmationError: value !== formData.newPassword,
      }));
    }
  };

  const validatePassword = (password: string) => {
    const errors: string[] = [];
    if (password.length < 8)
      errors.push(t("accountSecurity.passwordTooShort"));
    if (!/\d/.test(password))
      errors.push(t("accountSecurity.passwordNeedsNumber"));
    if (!/[a-z]/.test(password))
      errors.push(t("accountSecurity.passwordNeedsLowercase"));
    if (!/[A-Z]/.test(password))
      errors.push(t("accountSecurity.passwordNeedsUppercase"));
    return { isValid: errors.length === 0, errors };
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { password, newPassword, passwordConfirmation } = formData;
    const validationResult = validatePassword(newPassword);
    setPasswordValidationErrors(validationResult.errors);

    const newErrors = {
      passwordError: isGoogleOnly ? false : !password,
      newPasswordError: !newPassword || !validationResult.isValid,
      passwordConfirmationError:
        !passwordConfirmation || newPassword !== passwordConfirmation,
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some((e) => e)) return;

    setIsChangingPassword(true);
    try {
      if (isGoogleOnly) {
        // Google-only user: set password via server API (no current password needed)
        const result = await setPasswordApi({ idToken: idToken!, newPassword }).unwrap();
        dispatch(updateUser({ auth_provider: result.auth_provider }));
        dispatch(addToast({ type: "success", message: t("accountSecurity.passwordSetSuccess", "Password set successfully! You can now sign in with email and password.") }));
      } else {
        // Email user: change password via server API (verifies old password server-side)
        await changePasswordApi({ idToken: idToken!, oldPassword: password, newPassword }).unwrap();
        onPasswordChange({ password, newPassword });
        dispatch(addToast({ type: "success", message: t("accountSecurity.passwordChangedSuccess") }));
      }
      setFormData({ password: "", newPassword: "", passwordConfirmation: "" });
      setPasswordStrength(0);
      setPasswordValidationErrors([]);
    } catch (err: unknown) {
      Sentry.captureException(err);
      let errorMessage = t("accountSecurity.passwordChangeFailed");
      const rtkErr = err as { data?: { code?: string; error?: string; message?: string; auth_provider?: string } } | undefined;
      if (rtkErr?.data?.code === "NotAuthorizedException") {
        errorMessage = t("accountSecurity.incorrectCurrentPassword");
      } else if (rtkErr?.data?.auth_provider) {
        // If server says user is no longer Google-only, self-correct the session
        dispatch(updateUser({ auth_provider: rtkErr.data.auth_provider }));
        errorMessage = t("accountSecurity.passwordAlreadySet", "You already have a password. Please use the change password form.");
      } else if (isCognitoError(err)) {
        if (err.code === "NotAuthorizedException") {
          errorMessage = t("accountSecurity.incorrectCurrentPassword");
        } else if (err.code === "InvalidPasswordException") {
          errorMessage = t("accountSecurity.weakPassword");
        } else if (err.code === "LimitExceededException") {
          errorMessage = t("accountSecurity.tooManyAttempts");
        }
      }
      dispatch(togglePopup({ type: "ERROR", message: errorMessage }));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const { password, newPassword, passwordConfirmation } = formData;
  const { passwordError, newPasswordError, passwordConfirmationError } = errors;

  return (
    <div className={cx("accountSecurity")}>
      {/* Current Email */}
      <div className={cx("emailSection")}>
        <div className={cx("emailIcon")}>
          <i className="fas fa-envelope"></i>
        </div>
        <div className={cx("emailInfo")}>
          <span className={cx("emailLabel")}>
            {t("accountSecurity.currentEmail", "Email Address")}
          </span>
          <span className={cx("emailValue")}>{user?.email || "-"}</span>
        </div>
      </div>

      <div className={cx("divider")} />

      {/* Password Section */}
      <h3 className={cx("sectionTitle")}>
        {isGoogleOnly
          ? t("accountSecurity.setPassword", "Set Password")
          : t("accountSecurity.changePassword", "Change Password")}
      </h3>

      {isGoogleOnly && (
        <p className={cx("googleHint")}>
          {t(
            "accountSecurity.googleAccountHint",
            "Your account was created with Google. You can set a password to also sign in with email and password."
          )}
        </p>
      )}

      <form onSubmit={handlePasswordSubmit}>
        {/* Current password field — only for email users */}
        {!isGoogleOnly && (
          <div className={cx("inputGroup")}>
            <label className={cx("inputLabel")}>
              {t("accountSecurity.currentPassword", "Current Password")}
            </label>
            <div className={cx("inputWrapper")}>
              <input
                name="password"
                type={showCurrentPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                className={cx("input", { inputError: passwordError })}
                onChange={handleInputChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={t(
                  "accountSecurity.enterCurrentPassword",
                  "Enter current password"
                )}
              />
              {password && (
                <button
                  type="button"
                  className={cx("passwordToggle")}
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  tabIndex={-1}
                >
                  <EyeIcon visible={showCurrentPassword} />
                </button>
              )}
            </div>
            {passwordError && (
              <span className={cx("errorText")}>
                {t(
                  "accountSecurity.enterCurrentPassword",
                  "Please enter your current password"
                )}
              </span>
            )}
          </div>
        )}

        <div className={cx("inputGroup")}>
          <label className={cx("inputLabel")}>
            {isGoogleOnly
              ? t("accountSecurity.password", "Password")
              : t("accountSecurity.newPassword", "New Password")}
          </label>
          <div className={cx("inputWrapper")}>
            <input
              name="newPassword"
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              value={newPassword}
              className={cx("input", { inputError: newPasswordError })}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t(
                "accountSecurity.enterNewPassword",
                "Enter new password"
              )}
            />
            {newPassword && (
              <button
                type="button"
                className={cx("passwordToggle")}
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
              >
                <EyeIcon visible={showNewPassword} />
              </button>
            )}
          </div>
          {newPasswordError && passwordValidationErrors.length > 0 && (
            <div className={cx("validationErrors")}>
              {passwordValidationErrors.map((error, index) => (
                <div key={index} className={cx("validationItem")}>
                  <i className="fas fa-times"></i>
                  <span>{error}</span>
                </div>
              ))}
            </div>
          )}
          {newPassword && (
            <div className={cx("strengthSection")}>
              <div className={cx("strengthBar")}>
                {[1, 2, 3, 4].map((level) => (
                  <div
                    key={level}
                    className={cx("strengthSegment")}
                    style={{
                      backgroundColor:
                        level <= passwordStrength
                          ? getStrengthColor()
                          : "rgba(160, 152, 174, 0.2)",
                    }}
                  />
                ))}
              </div>
              <span
                className={cx("strengthLabel")}
                style={{ color: getStrengthColor() }}
              >
                {getStrengthLabel()}
              </span>
            </div>
          )}
        </div>

        <div className={cx("inputGroup")}>
          <label className={cx("inputLabel")}>
            {t("accountSecurity.confirmPassword", "Confirm New Password")}
          </label>
          <div className={cx("inputWrapper")}>
            <input
              name="passwordConfirmation"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              value={passwordConfirmation}
              className={cx("input", {
                inputError: passwordConfirmationError,
              })}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={t(
                "accountSecurity.confirmNewPassword",
                "Re-enter new password"
              )}
            />
            {passwordConfirmation && (
              <button
                type="button"
                className={cx("passwordToggle")}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                tabIndex={-1}
              >
                <EyeIcon visible={showConfirmPassword} />
              </button>
            )}
          </div>
          {passwordConfirmationError && (
            <span className={cx("errorText")}>
              {t(
                "accountSecurity.passwordsDoNotMatch",
                "Passwords do not match"
              )}
            </span>
          )}
        </div>

        <button
          type="submit"
          className={cx("changePasswordButton")}
          disabled={isChangingPassword}
        >
          {isChangingPassword ? (
            <span>
              {t("accountSecurity.changingPassword", "Changing...")}
            </span>
          ) : (
            <>
              <i className="fas fa-key"></i>
              <span>
                {isGoogleOnly
                  ? t("accountSecurity.setPassword", "Set Password")
                  : t("accountSecurity.changePassword", "Change Password")}
              </span>
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default AccountSecurity;
